import { createFileRoute } from '@tanstack/react-router';
import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PermissionGate } from '@/components/PermissionGate';
import { useRealtimeAssets } from '@/features/realtime/hooks/useRealtimeAssets';
import { useUnregisteredDevices } from '@/features/realtime/hooks/useUnregisteredDevices';
import { UnregisteredDevicesSection } from '@/features/realtime/components/UnregisteredDevicesSection';
import { LocationGroup } from '@/features/realtime/components/LocationGroup';
import { AssetRealtimeModal } from '@/features/realtime/components/AssetRealtimeModal';
import { RealtimeAsset } from '@/features/realtime/services/realtime.service';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Activity, AlertTriangle, Wifi, WifiOff, Loader2, type LucideIcon } from 'lucide-react';
import { categoriesService, Category } from '@/features/assets/services/categories.service';
import {
  RequestFormDialog,
  type RequestFormSubmitData,
} from '@/features/requests/components/RequestFormDialog';
import { requestsService } from '@/features/requests/services/requests.service';
import { Asset } from '@/features/assets/services/assets.service';
import { sortAssetsOnlineFirst, sortLocationGroups } from '@/features/realtime/lib/sort';

export const Route = createFileRoute('/_authenticated/realtime')({
  component: RealtimePage,
});

type StatusFilter = 'online' | 'offline' | 'inUse' | 'available' | 'overdue';

const STATUS_PREDICATES: Record<StatusFilter, (asset: RealtimeAsset) => boolean> = {
  online: (asset) => asset.isOnline,
  offline: (asset) => !asset.isOnline,
  inUse: (asset) => asset.inUseBy !== null,
  available: (asset) => asset.inUseBy === null,
  overdue: (asset) => !!asset.inUseBy?.isOverdue,
};

interface StatCardProps {
  title: string;
  value: number;
  icon: LucideIcon;
  iconClassName: string;
  valueClassName?: string;
  active: boolean;
  onClick: () => void;
}

/** Card de número que filtra a lista ao clicar (clicar de novo limpa). */
function StatCard({
  title,
  value,
  icon: Icon,
  iconClassName,
  valueClassName,
  active,
  onClick,
}: StatCardProps) {
  return (
    <Card
      role="button"
      tabIndex={0}
      aria-pressed={active}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      className={`cursor-pointer transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
        active ? 'ring-2 ring-primary' : ''
      }`}
    >
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <Icon className={`h-4 w-4 ${iconClassName}`} />
      </CardHeader>
      <CardContent>
        <div className={`text-2xl font-bold ${valueClassName ?? ''}`}>{value}</div>
      </CardContent>
    </Card>
  );
}

function RealtimePage() {
  const queryClient = useQueryClient();
  const { assets, stats, isLoading, isConnected, lastPing } = useRealtimeAssets();
  const { devices: unregisteredDevices } = useUnregisteredDevices();
  const [selectedAsset, setSelectedAsset] = useState<RealtimeAsset | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState<string | undefined>(undefined);
  const [statusFilter, setStatusFilter] = useState<StatusFilter | undefined>(undefined);
  const [requestOpen, setRequestOpen] = useState(false);
  const [assetForRequest, setAssetForRequest] = useState<RealtimeAsset | null>(null);

  // Buscar categorias
  const { data: categoriesData } = useQuery({
    queryKey: ['categories'],
    queryFn: async () => {
      const response = await categoriesService.getAll();
      return Array.isArray(response.data) ? response.data : [];
    },
  });

  // O Realtime só lista equipamentos de categorias rastreáveis
  const categories: Category[] = (categoriesData || []).filter((c: Category) => c.isTrackable);

  const toggleStatusFilter = (value: StatusFilter | undefined) =>
    setStatusFilter((current) => (current === value ? undefined : value));

  // Aplicar filtros
  const filteredAssets = useMemo(() => {
    let filtered = [...assets];

    // Filtro de categoria
    if (categoryFilter) {
      filtered = filtered.filter((asset) => asset.categoryId === categoryFilter);
    }

    if (statusFilter) {
      filtered = filtered.filter(STATUS_PREDICATES[statusFilter]);
    }

    return filtered;
  }, [assets, categoryFilter, statusFilter]);

  // Agrupar por localização após filtros
  const groupedByLocationFiltered = useMemo(() => {
    const grouped = filteredAssets.reduce(
      (acc, asset) => {
        const location = asset.location || 'Sem localização';
        if (!acc[location]) {
          acc[location] = [];
        }
        acc[location].push(asset);
        return acc;
      },
      {} as Record<string, RealtimeAsset[]>,
    );

    // Online sempre primeiro, nos cards e na ordem dos locais
    const fluxo = sortAssetsOnlineFirst(grouped['Fluxo'] || []);
    const others = sortLocationGroups(
      Object.entries(grouped)
        .filter(([location]) => location !== 'Fluxo')
        .map(([location, assets]) => ({ location, assets: sortAssetsOnlineFirst(assets) })),
    );

    return { fluxo, others };
  }, [filteredAssets]);

  // Mutation para criar solicitação (com a foto do equipamento)
  const createRequestMutation = useMutation({
    mutationFn: (data: RequestFormSubmitData) =>
      requestsService.create({
        assetId: data.assetId,
        type: 'withdrawal',
        withdrawalLocation: data.withdrawalLocation,
        plannedReturnDate: data.plannedReturnDate,
        termsAccepted: data.termsAccepted,
        quantityRequested: data.quantityRequested,
        photos: data.photos,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['realtime-assets'] });
      setRequestOpen(false);
      setAssetForRequest(null);
    },
  });

  const handleRequest = (asset: RealtimeAsset, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    if (!asset.inUseBy) {
      setAssetForRequest(asset);
      setRequestOpen(true);
    }
  };

  const handleSubmitRequest = async (data: RequestFormSubmitData) => {
    await createRequestMutation.mutateAsync(data);
  };

  const handleAssetClick = (asset: RealtimeAsset) => {
    setSelectedAsset(asset);
    setIsModalOpen(true);
  };

  const getConnectionStatus = () => {
    if (!isConnected) {
      return { text: 'Desconectado', color: 'text-red-500', bg: 'bg-red-500/10' };
    }
    if (lastPing) {
      const secondsSincePing = Math.floor((Date.now() - lastPing.getTime()) / 1000);
      if (secondsSincePing < 10) {
        return { text: 'Conectado', color: 'text-green-500', bg: 'bg-green-500/10' };
      }
      return { text: 'Lento', color: 'text-yellow-500', bg: 'bg-yellow-500/10' };
    }
    return { text: 'Conectado', color: 'text-green-500', bg: 'bg-green-500/10' };
  };

  const status = getConnectionStatus();

  return (
    <PermissionGate permission="realtime.view">
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Dashboard Realtime</h1>
            <p className="text-muted-foreground">Acompanhe equipamentos em tempo real</p>
          </div>
          <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${status.bg}`}>
            <div
              className={`w-2 h-2 rounded-full ${status.color.replace('text-', 'bg-')} animate-pulse`}
            />
            <span className={`text-sm font-medium ${status.color}`}>
              {status.text}
              {lastPing && ` • ${Math.floor((Date.now() - lastPing.getTime()) / 1000)}s`}
            </span>
          </div>
        </div>

        {/* Estatísticas gerais */}
        {/* Estatísticas: clicar filtra a lista */}
        <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
          <StatCard
            title="Total"
            value={stats.total}
            icon={Activity}
            iconClassName="text-muted-foreground"
            active={!statusFilter}
            onClick={() => setStatusFilter(undefined)}
          />
          <StatCard
            title="Online"
            value={stats.online}
            icon={Wifi}
            iconClassName="text-green-500"
            valueClassName="text-green-600"
            active={statusFilter === 'online'}
            onClick={() => toggleStatusFilter('online')}
          />
          <StatCard
            title="Offline"
            value={stats.offline}
            icon={WifiOff}
            iconClassName="text-gray-500"
            valueClassName="text-gray-600"
            active={statusFilter === 'offline'}
            onClick={() => toggleStatusFilter('offline')}
          />
          <StatCard
            title="Em Uso"
            value={stats.inUse}
            icon={Activity}
            iconClassName="text-blue-500"
            valueClassName="text-blue-600"
            active={statusFilter === 'inUse'}
            onClick={() => toggleStatusFilter('inUse')}
          />
          <StatCard
            title="Atrasados"
            value={stats.overdue}
            icon={AlertTriangle}
            iconClassName="text-red-500"
            valueClassName="text-red-600"
            active={statusFilter === 'overdue'}
            onClick={() => toggleStatusFilter('overdue')}
          />
        </div>

        {/* Filtros */}
        <Card>
          <CardHeader>
            <CardTitle>Filtros</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col gap-4 md:flex-row">
              <Select
                value={categoryFilter || 'all'}
                onValueChange={(value) => setCategoryFilter(value === 'all' ? undefined : value)}
              >
                <SelectTrigger className="w-full md:w-[200px]">
                  <SelectValue placeholder="Todas as categorias" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as categorias</SelectItem>
                  {categories.map((category) => (
                    <SelectItem key={category.id} value={category.id}>
                      {category.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={statusFilter || 'all'}
                onValueChange={(value) =>
                  setStatusFilter(value === 'all' ? undefined : (value as StatusFilter))
                }
              >
                <SelectTrigger className="w-full md:w-[200px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="online">Online</SelectItem>
                  <SelectItem value="offline">Offline</SelectItem>
                  <SelectItem value="available">Disponíveis</SelectItem>
                  <SelectItem value="inUse">Em Uso</SelectItem>
                  <SelectItem value="overdue">Devolução atrasada</SelectItem>
                </SelectContent>
              </Select>

              {(categoryFilter || statusFilter) && (
                <Button
                  variant="outline"
                  onClick={() => {
                    setCategoryFilter(undefined);
                    setStatusFilter(undefined);
                  }}
                >
                  Limpar Filtros
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            {/* PCs com o app no tenant, mas sem equipamento */}
            <UnregisteredDevicesSection devices={unregisteredDevices} />

            {/* Área Fluxo */}
            {groupedByLocationFiltered.fluxo.length > 0 && (
              <LocationGroup
                location="Fluxo"
                assets={groupedByLocationFiltered.fluxo}
                onAssetClick={handleAssetClick}
                onRequest={handleRequest}
              />
            )}

            {/* Outras localizações */}
            {groupedByLocationFiltered.others.map(({ location, assets }) => (
              <LocationGroup
                key={location}
                location={location}
                assets={assets}
                onAssetClick={handleAssetClick}
                onRequest={handleRequest}
              />
            ))}

            {/* Mensagem se não houver equipamentos */}
            {filteredAssets.length === 0 && (
              <Card>
                <CardContent className="py-12 text-center">
                  <p className="text-muted-foreground">Nenhum equipamento encontrado</p>
                  {assets.length === 0 && (
                    <p className="mt-2 text-sm text-muted-foreground">
                      O Realtime mostra só equipamentos de categorias marcadas como rastreáveis
                      (Categorias → editar → Rastreável).
                    </p>
                  )}
                </CardContent>
              </Card>
            )}
          </>
        )}

        {/* Modal de Solicitação */}
        <RequestFormDialog
          open={requestOpen}
          onOpenChange={setRequestOpen}
          asset={
            assetForRequest
              ? ({
                  id: assetForRequest.id,
                  tenantId: assetForRequest.tenantId,
                  name: assetForRequest.name,
                  code: assetForRequest.code,
                  location: assetForRequest.location,
                  quantity: assetForRequest.quantity,
                  available: assetForRequest.quantity,
                  autoApproveWithdrawal: assetForRequest.autoApproveWithdrawal,
                  createdAt: assetForRequest.createdAt,
                  updatedAt: assetForRequest.updatedAt,
                } as Asset)
              : null
          }
          onSubmit={handleSubmitRequest}
          isLoading={createRequestMutation.isPending}
        />

        {/* Modal de detalhes */}
        <AssetRealtimeModal
          asset={selectedAsset}
          open={isModalOpen}
          onOpenChange={setIsModalOpen}
        />
      </div>
    </PermissionGate>
  );
}
